import React, { useState, useMemo } from 'react';
import {
  Box,
  Heading,
  Text,
  Card,
  CardHeader,
  CardContent,
  DataTable,
  Button,
  Flex,
  Select,
  TextField,
  NumberField,
  TextArea,
  LoadingOrbit,
  type SelectOption,
} from '@chatsdet/ui';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import {
  useHintsAdminQuery,
  useCreateHintMutation,
  useUpdateHintMutation,
  useDeleteHintMutation,
  type AdminHint,
} from './useSuperadminData';

const HINT_LOCATIONS: SelectOption[] = [{ id: 'flow-designer', label: 'Flow Designer' }];

export function SuperadminHints() {
  const hintsQuery = useHintsAdminQuery();
  const createMutation = useCreateHintMutation();
  const updateMutation = useUpdateHintMutation();
  const deleteMutation = useDeleteHintMutation();

  const [editingHint, setEditingHint] = useState<AdminHint | null>(null);
  const [location, setLocation] = useState('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [sortOrder, setSortOrder] = useState(0);

  const columns = useMemo(
    (): ColumnDef<StockFeatures, AdminHint>[] => [
      { accessorKey: 'location', header: 'Location', size: 150 },
      { accessorKey: 'title', header: 'Title', size: 200 },
      {
        accessorKey: 'content',
        header: 'Content',
        size: 300,
        cell: ({ getValue }) => {
          const val = getValue() as string;
          return val.length > 80 ? val.slice(0, 80) + '...' : val;
        },
      },
      { accessorKey: 'sortOrder', header: 'Order', size: 80 },
    ],
    []
  );

  const startEdit = (hint: AdminHint) => {
    setEditingHint(hint);
    setLocation(hint.location);
    setTitle(hint.title);
    setContent(hint.content);
    setSortOrder(hint.sortOrder);
  };

  const resetForm = () => {
    setEditingHint(null);
    setLocation('');
    setTitle('');
    setContent('');
    setSortOrder(0);
  };

  const handleSave = () => {
    if (editingHint) {
      updateMutation.mutate(
        { id: editingHint.id, location, title, content, sortOrder },
        { onSuccess: resetForm }
      );
    } else {
      createMutation.mutate({ location, title, content, sortOrder }, { onSuccess: resetForm });
    }
  };

  const handleDelete = (id: string) => {
    if (confirm('Delete this hint?')) {
      deleteMutation.mutate(id);
    }
  };

  const hints = hintsQuery.data?.hints ?? [];

  return (
    <Box className="space-y-6">
      <Card padding="lg">
        <CardHeader>
          <Heading level={3}>Hints</Heading>
          <Text className="text-muted-foreground">
            Manage hint cards shown to users at specific locations.
          </Text>
        </CardHeader>
        <CardContent>
          {hintsQuery.isLoading ? (
            <LoadingOrbit />
          ) : (
            <DataTable columns={columns} data={hints} onRowClick={(row) => startEdit(row)} />
          )}
        </CardContent>
      </Card>

      <Card padding="lg">
        <CardHeader>
          <Heading level={4}>{editingHint ? 'Edit Hint' : 'Create Hint'}</Heading>
        </CardHeader>
        <CardContent>
          <Flex direction="column" gap="md">
            <Select
              label="Location"
              items={HINT_LOCATIONS}
              selectedKey={location}
              onSelectionChange={(key) => setLocation(key as string)}
              placeholder="Select a location"
            />
            <TextField label="Title" value={title} onChange={setTitle} placeholder="Hint title" />
            <TextArea
              label="Content"
              value={content}
              onChange={setContent}
              placeholder="Hint content (supports plain text)"
            />
            <NumberField
              label="Sort Order"
              value={sortOrder}
              onChange={(v) => setSortOrder(v ?? 0)}
            />
            <Flex gap="sm">
              <Button
                variant="primary"
                onPress={handleSave}
                isDisabled={!location || !title || !content}
              >
                {editingHint ? 'Update' : 'Create'}
              </Button>
              {editingHint && (
                <>
                  <Button variant="outline" onPress={resetForm}>
                    Cancel
                  </Button>
                  <Button variant="destructive" onPress={() => handleDelete(editingHint.id)}>
                    Delete
                  </Button>
                </>
              )}
            </Flex>
          </Flex>
        </CardContent>
      </Card>
    </Box>
  );
}
